// Archivo para mostrar una vista previa del layout
// LayoutPreview.jsx

import React, { useMemo, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, Button, Backdrop, CircularProgress, Paper, useTheme } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import PrintIcon from '@mui/icons-material/Print'
import {
  normalizeFieldPosition,
  parseLayoutConfig,
  resolveLayoutValue,
} from '../../utils/layoutUtils'
import WorkflowStageBanner from '../workflow/WorkflowStageBanner'

const isWorkflowStageBannerField = (field = {}) => {
  const type = String(field?.type || '').trim().toLowerCase()
  const role = String(field?.role || '').trim().toLowerCase()
  const name = String(field?.name || '').trim().toLowerCase()
  return (
    type === 'workflow_stage_banner' ||
    type === 'billing_stage_banner' ||
    role === 'workflow_stage_banner' ||
    name === 'workflow_stage_banner'
  )
}

const normalizeBannerConfig = (field = {}) => {
  const fieldConfig = field?.config && typeof field.config === 'object' ? field.config : {}
  const legacyConfig = {
    resolverKey: field?.resolverKey,
    mode: field?.mode,
    prefix: field?.prefix,
    showStageNumber: field?.showStageNumber,
    showQuotes: field?.showQuotes,
    shape: field?.shape,
    emptyStateMode: field?.emptyStateMode,
    emptyBehavior: field?.emptyBehavior,
    emptyMessage: field?.emptyMessage,
    customLabel: field?.customLabel,
    heading: field?.heading,
    title: field?.title,
    showMetrics: field?.showMetrics,
  }
  const config = { ...legacyConfig, ...fieldConfig }

  const resolverKey = config.resolverKey || 'workflow.current_stage'
  const mode =
    config.mode ||
    (resolverKey === 'workflow.current_billable_stage'
      ? 'billable_stage'
      : 'progress_stage')

  return {
    resolverKey,
    mode,
    prefix: config.prefix || 'ETAPA',
    showStageNumber: config.showStageNumber !== false,
    showQuotes: Boolean(config.showQuotes),
    shape: config.shape || 'rounded',
    emptyStateMode: config.emptyStateMode || config.emptyBehavior || 'message',
    emptyMessage: config.emptyMessage || '',
    customLabel: config.customLabel || config.heading || config.title || field?.label || '',
    showMetrics: config.showMetrics !== false,
  }
}

const fieldPaperStyles = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  width: '100%',
  height: '100%',
  p: 1.25,
  overflow: 'hidden',
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 1,
  backgroundColor: 'background.paper',
}

/**
 * @param {Array}  layoutConfig - definición de campos del layout
 * @param {Object} values       - (opcional) valores reales a mostrar en lugar de placeholders
 */
export default function LayoutPreview({ layoutConfig = [], values = {} }) {
  const theme = useTheme()
  const previewRef = useRef(null)
  const [processing, setProcessing] = useState(false)

  const normalizedLayout = useMemo(
    () =>
      parseLayoutConfig(layoutConfig)
        .filter((field) => field && typeof field === 'object')
        .map((field) => normalizeFieldPosition(field)),
    [layoutConfig]
  )


  /* ------------------------------------------------------------
   * Helpers de exportar / imprimir (carga diferida de html2canvas)
   * -----------------------------------------------------------*/
  const captureCanvas = async () => {
    if (!previewRef.current) return null
    setProcessing(true)
    try {
      const { default: html2canvas } = await import('html2canvas')
      return await html2canvas(previewRef.current)
    } finally {
      setProcessing(false)
    }
  }

  const exportAsImage = async () => {
    const canvas = await captureCanvas()
    if (!canvas) return
    const link = document.createElement('a')
    link.href = canvas.toDataURL('image/png')
    link.download = 'layout_preview.png'
    link.click()
  }

  const printPreview = async () => {
    const canvas = await captureCanvas()
    if (!canvas) return
    const imgData = canvas.toDataURL('image/png')
    const win = window.open('')
    if (!win) return
    win.document.write(`<img src="${imgData}" style="width:100%"/>`)
    win.document.close()
    win.focus()
    win.print()
    win.close()
  }

  /* ------------------------------------------------------------
   * Render helpers
   * -----------------------------------------------------------*/
  const renderFieldContent = (field) => {
    const value = resolveLayoutValue(field, values)

    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return (
        <Typography sx={{ fontSize: field.fontSize, fontWeight: 500, lineHeight: 1.25, wordBreak: 'break-word' }}>
          {value}
        </Typography>
      )
    }

    return (
      <Typography sx={{ fontSize: field.fontSize, fontWeight: 500, color: 'text.secondary' }}>—</Typography>
    )
  }

  return (

    <Box sx={{ p: 0 }}>
      {/* Barra de acciones */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={exportAsImage}
          disabled={processing}
          aria-label="Exportar vista previa como imagen"
        >
          Exportar
        </Button>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={printPreview}
          disabled={processing}
          aria-label="Imprimir vista previa"
        >
          Imprimir
        </Button>
      </Box>

      {/* Contenedor de la vista previa */}
      <Box
        ref={previewRef}
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: 600,
          border: '2px solid',
          borderColor: 'divider',
          borderRadius: 3,
          bgcolor:
            theme.palette.mode === 'dark'
              ? theme.palette.background.paper
              : '#ffffff',
          boxShadow: 2,
          backgroundImage:
            'linear-gradient(180deg, rgba(25,118,210,0.02) 0%, rgba(15,23,42,0.01) 100%)',
          overflow: 'hidden',
        }}
      >
        {normalizedLayout.map((field) => {
          const isWorkflowBanner = isWorkflowStageBannerField(field)
          const bannerConfig = normalizeBannerConfig(field)

          return (
            <Box
              key={field.id || `${field.name}-${field.x}-${field.y}`}
              sx={{
                position: 'absolute',
                top: field.y,
                left: field.x,
                width: field.width,
                height: field.height,
                boxSizing: 'border-box',
              }}
            >
              {isWorkflowBanner ? (
                <WorkflowStageBanner
                  workflowState={values?.workflowPayload || null}
                  resolvers={
                    values?.workflowResolverPayload ||
                    values?.workflowResolversRaw ||
                    values?.workflow ||
                    null
                  }
                  config={bannerConfig}
                />
              ) : (
                <Paper sx={fieldPaperStyles}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.1,
                      mb: 0.5,
                      wordBreak: 'break-word',
                    }}
                  >
                    {field.label}
                  </Typography>
                  {renderFieldContent(field)}
                </Paper>
              )}
            </Box>
          )
        })}
      </Box>

      {/* Loading overlay mientras se genera el canvas */}
      <Backdrop open={processing} sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  )
}

LayoutPreview.propTypes = {
  layoutConfig: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired, // clave interna (NO se renderiza)
      label: PropTypes.string.isRequired, // texto visible
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
      backgroundColor: PropTypes.string,
      color: PropTypes.string,
      fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  values: PropTypes.object, // valores opcionales del registro a mostrar
}
